import { ChangeDetectionStrategy, Component, ContentChild, effect, OnDestroy, TemplateRef, untracked, ViewEncapsulation } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { SplitterResizeEndEvent, SplitterResizeStartEvent } from 'primeng/splitter';
import { PlycaUiBannerGenericComponent } from '@plyca/ui-library/components/organisms/banners/banner-generic/banner-generic.component';
import { PlycaUiButtonGenericComponent } from '@plyca/ui-library/components/atoms/buttons/button-generic/button-generic.component';
import { PlycaUiSplitterComponent } from '@plyca/ui-library/components/organisms/splitter/splitter.component';
import { HelpElementResultComponent } from '@/app/components/common/help/help-element-result/help-element-result.component';
import { GeneralHelpComponent } from '@/app/components/common/help/general-help/general-help.component';
import { HeaderComponent } from '@/app/components/common/header/header.component';
import { RoutingPaths } from '@/app/common/routing';
import { FollowTopHelpDirective } from '../../features/directives/dom/follow-top-help.directive';
import { LayoutAbstractComponent } from './_config/layout.abstract';
import { GeneralHelpConst } from './_config/layout.const';
import { HelpType } from '@/app/components/common/help/_config/help-type.const';
import { PlycaUiButtonHistoryComponent } from '@plyca/ui-library';
import { CONFIG_JSON_KEYS } from '@/app/_core/domain/const/config-json-keys.const';
import { parseSplitterSize } from './_config/layout.utils';

@Component({
  selector: 'plyca-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgTemplateOutlet,
    TranslateModule,
    HeaderComponent,
    HelpElementResultComponent,
    GeneralHelpComponent,
    PlycaUiSplitterComponent,
    PlycaUiButtonGenericComponent,
    PlycaUiBannerGenericComponent,
    PlycaUiButtonHistoryComponent,
    FollowTopHelpDirective
  ]
})
export class LayoutComponent extends LayoutAbstractComponent implements OnDestroy {
  @ContentChild(TemplateRef, { static: true }) panelTemplate!: TemplateRef<any>;

  constructor() {
    super();
    this._effects();
  }

  private _effects(): void {
    this._helpEffect();
    this._changeLanguageEffect();
  }

  private _helpEffect(): void {
    effect(
      () => {
        const isDesktop = this.isDesktop();
        if (!isDesktop) return;

        const tagsStr = this.searchTagsSig().toString();
        const pageTagsStr = this.pageTagsSig().toString();

        if (tagsStr || pageTagsStr) {
          this.controlGetHelp();
        }
      },
      { allowSignalWrites: true }
    );
  }

  private _changeLanguageEffect(): void {
    effect(
      () => {
        if (this.helpUseCases.selectHelpSig().isChangedLanguage) {
          this.refreshHelp();
          if (!this.isHelpOpen()) {
            this.controlGetHelp();
          }
          untracked(() => {
            this.helpUseCases.setHelp({ ...this.helpUseCases.selectHelpSig(), isChangedLanguage: false });
          });
        }
      },
      { allowSignalWrites: true }
    );
  }

  public backHelp(): void {
    const currentHelp = this.helpUseCases.selectHelpSig();
    const history = [...(currentHelp.history || [])];
    const future = [...(currentHelp.future || [])];

    if (history.length > 0) {
      const isCurrentInHistory = history[history.length - 1].tags.toString() === currentHelp.currentTag;
      const historyToOp = isCurrentInHistory ? history : [...history, { tags: [currentHelp.currentTag], data: currentHelp.listData || [] }];

      if (historyToOp.length > 1) {
        const currentItem = historyToOp.pop();
        if (currentItem && isCurrentInHistory) {
          const scrollContainerBack = (document.getElementById('refScroll') || document.querySelector('.layout__container-block')) as HTMLElement;
          future.push({ tags: currentItem.tags, data: currentHelp.listData || [], scrollTop: scrollContainerBack?.scrollTop ?? 0 });
        }

        const prevState = historyToOp[historyToOp.length - 1];
        this.helpUseCases.setHelp({
          ...currentHelp,
          history: historyToOp,
          future: future,
          listData: prevState.data,
          currentTag: prevState.tags.toString(),
          isGeneralHelp: prevState.tags.toString() === HelpType.GENERAL_HELP,
          selectedGeneralHelpNodeId: prevState.selectedNodeId,
          context: { searchTags: prevState.tags, helpElements: [] },
          extraData: { ...currentHelp?.extraData, isRefreshCounter: false }
        });

        if (prevState.scrollTop !== undefined) {
          setTimeout(() => {
            const container = (document.getElementById('refScroll') || document.querySelector('.layout__container-block')) as HTMLElement;
            container?.scrollTo({ top: prevState.scrollTop, behavior: 'smooth' });
          }, 100);
        }
      }
    }
  }

  public forwardHelp(): void {
    const currentHelp = this.helpUseCases.selectHelpSig();
    const history = [...(currentHelp.history || [])];
    const future = [...(currentHelp.future || [])];

    if (future.length > 0) {
      const nextItem = future.pop();
      if (nextItem) {
        history.push(nextItem);
        this.helpUseCases.setHelp({
          ...currentHelp,
          history: history,
          future: future,
          listData: nextItem.data,
          currentTag: nextItem.tags.toString(),
          isGeneralHelp: nextItem.tags.toString() === HelpType.GENERAL_HELP,
          selectedGeneralHelpNodeId: nextItem.selectedNodeId,
          context: { searchTags: nextItem.tags, helpElements: [] },
          extraData: { ...currentHelp?.extraData, isRefreshCounter: false }
        });

        if (nextItem.scrollTop !== undefined) {
          setTimeout(() => {
            const container = (document.getElementById('refScroll') || document.querySelector('.layout__container-block')) as HTMLElement;
            container?.scrollTo({ top: nextItem.scrollTop, behavior: 'smooth' });
          }, 100);
        }
      }
    }
  }

  public refreshHelp(): void {
    const currentHelp = this.helpUseCases.selectHelpSig();

    if (this.isGeneralRoute()) {
      const history = [...(currentHelp.history || [])];
      const firstNonGeneral = history.find((item) => item.tags.toString() !== HelpType.GENERAL_HELP);
      if (firstNonGeneral) {
        this.helpUseCases.setHelp({
          ...currentHelp,
          history: [firstNonGeneral],
          future: [],
          listData: firstNonGeneral.data,
          currentTag: firstNonGeneral.tags.toString(),
          isGeneralHelp: false,
          selectedGeneralHelpNodeId: null,
          context: { searchTags: firstNonGeneral.tags, helpElements: [] },
          extraData: { ...currentHelp?.extraData, isRefreshCounter: false }
        });
        return;
      }
    }

    const pageTags = currentHelp.isGeneralHelp && currentHelp.pageContextTag ? [currentHelp.pageContextTag] : this.pageTagsSig();
    this.helpUseCases.setHelp({
      ...currentHelp,
      history: [{ tags: pageTags, data: [] }],
      future: [],
      listData: [],
      currentTag: '',
      isGeneralHelp: false,
      context: { searchTags: pageTags, helpElements: [] },
      extraData: { ...currentHelp?.extraData, isRefreshCounter: true }
    });
    this.refreshCounter.update((v) => v + 1);
  }

  public closeHelp(): void {
    const defaultHelp = this.helpUseCases.selectHelpSig();
    this.helpUseCases.setHelp({
      ...defaultHelp,
      isOpenHelp: false,
      isCloseHelp: true,
      history: [],
      future: [],
      listData: [],
      currentTag: ''
    });
  }

  public onHistorySelect(event: any): void {
    const { index, item } = event || {};

    if (index === undefined || !item) {
      return;
    }

    const currentHelp = this.helpUseCases.selectHelpSig();
    const history = [...(currentHelp.history || [])];
    const newHistory = history.slice(0, index + 1);

    this.helpUseCases.setHelp({
      ...currentHelp,
      history: newHistory,
      future: [],
      listData: item.data,
      currentTag: item.tags.toString(),
      isGeneralHelp: item.tags.toString() === HelpType.GENERAL_HELP,
      selectedGeneralHelpNodeId: item.selectedNodeId,
      context: { searchTags: item.tags, helpElements: [] },
      extraData: { ...currentHelp?.extraData, isRefreshCounter: false }
    });

    if (item.scrollTop !== undefined) {
      setTimeout(() => {
        const container = (document.getElementById('refScroll') || document.querySelector('.layout__container-block')) as HTMLElement;
        container?.scrollTo({ top: item.scrollTop, behavior: 'smooth' });
      }, 100);
    }
  }

  public startResize(_e: SplitterResizeStartEvent): void {
    if (this.openedThisDrag()) return;
    this.openedThisDrag.set(true);
    const currentHelp = this.helpUseCases.selectHelpSig();
    if (!currentHelp.isOpenHelp) {
      this.helpUseCases.setHelp({
        ...currentHelp,
        isOpenHelp: true,
        isCloseHelp: false,
        extraData: { ...currentHelp?.extraData, isRefreshCounter: true }
      });
    }
  }

  public endResize(_e: SplitterResizeEndEvent): void {
    if (!this.openedThisDrag()) return;

    if (_e.sizes && _e.sizes.length === 2) {
      if (_e.sizes[1] > 1) {
        let finalSizes = _e.sizes;
        // Respetar el máximo configurado en config.json
        const rawSplitterSize = this.appConfig.getValue(CONFIG_JSON_KEYS.splitterLayoutSize);
        const defaultConfigSize = parseSplitterSize(rawSplitterSize);
        const minMainSize = defaultConfigSize[0];

        if (finalSizes[0] < minMainSize) {
          finalSizes = [minMainSize, 100 - minMainSize];
        }

        const currentHelp = this.helpUseCases.selectHelpSig();
        if (!currentHelp.panelSize || JSON.stringify(currentHelp.panelSize) !== JSON.stringify(finalSizes)) {
          this.helpUseCases.setHelp({ ...currentHelp, panelSize: finalSizes });
        }
      } else {
        this.closeHelp();
      }
    }
    this.openedThisDrag.set(false);
  }

  public onNavigateGeneralHelp(): void {
    this.helpUseCases.setHelp({
      ...this.helpUseCases.selectHelpSig(),
      isOpenHelp: false,
      isGeneralHelp: true,
      history: [],
      future: [],
      listData: []
    });
    this.router.navigate([RoutingPaths.LoggedInGeneralHelp]);
  }

  public onClickScrollToTop(): void {
    if (document.getElementById(GeneralHelpConst.RefScroll)) {
      document.getElementById(GeneralHelpConst.RefScroll).scrollTo({ top: 0, behavior: 'smooth' });
    }
    const layoutContainer = document.querySelector('.layout__container-block');
    if (layoutContainer) {
      layoutContainer.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }
}
